import { useDebounceFn, useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import React from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { Button } from '@/app/components/ui/button';
import { DialogFooter } from '@/app/components/ui/dialog';
import type { ElementType, TextFormState } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { MAX_INPUT_LENGTH, MAX_TEXTAREA_INPUT_LENGTH } from '../constants';
import { useElementDialogApi } from '../hooks/use-element-dialog-api';
import type { MaterialV1Label } from '../types';
import { ChooseLabelSelect } from './ChooseLabelSelect';
import { DialogFooterTip } from './DialogFooterTip';
import { InputField } from './InputField';
import { TextAreaField } from './TextAreaField';

const UPDATE_TIME = 500;

interface TextElementFormProps {
  /** 初始数据 */
  initialData?: TextFormState;
  /** 标签列表 */
  labels: MaterialV1Label[] | undefined;
  /** 添加标签回调 */
  handleAddLabel: (label: MaterialV1Label) => void;
  /** 保存回调 */
  onSave: (element: ElementType) => void;
  /** 是否为编辑模式 */
  isEditMode?: boolean;
}

/**
 * 文字模式的表单组件（内部管理状态）
 * 只包含名称和描述字段
 */
export const TextElementForm = React.memo<TextElementFormProps>(
  ({ initialData, labels, handleAddLabel, onSave, isEditMode = false }) => {
    const t = useTranslations();

    // 使用 useSetState 管理状态
    const [state, setState] = useSetState<TextFormState>(
      initialData || {
        modality: 'text',
      },
    );

    // 使用统一的 API hook
    const { nameRef, checkLoading, nameCheckMsg, setNameCheckMsg, submitLoading, submit } = useElementDialogApi({
      state,
      initialName: initialData?.name,
      isEditMode,
      onSave,
      onStateUpdate: (updates) => {
        setState(updates as TextFormState);
      },
    });

    const { run: updateDescription, flush: flushUpdateDescription } = useDebounceFn(
      (description?: string) => {
        setState({ description });
      },
      {
        wait: UPDATE_TIME,
      },
    );

    return (
      <>
        <div
          id="element-main"
          className="hide-scrollbar flex flex-1 flex-col gap-3 overflow-y-scroll px-4 sm:pb-[120px]"
        >
          {/* 封面预览 */}
          <div className="mt-4 mb-2 flex flex-col items-center gap-1">
            <TextElementCover name={state?.name} className="size-[140px] text-3xl" />
            <div className="text-system-white/40 text-xs">{t('elements.elementDialog.cover')}</div>
          </div>

          <InputField
            initialValue={state?.name}
            ref={nameRef}
            fieldClassName="bg-system-black36 h-11"
            labelClassName="sm:text-sm"
            readOnly={false}
            placeholder={t('elements.elementDialog.namePlaceholder')}
            onChange={(e) => {
              setNameCheckMsg('');
              setState({ name: e.target.value });
            }}
            checking={checkLoading}
            errMsg={nameCheckMsg}
            label={t('elements.elementDialog.name')}
            maxLength={MAX_INPUT_LENGTH}
          />

          {/* 标签选择 */}
          <div>
            <div className="mb-2 text-white text-xs sm:text-sm">{t('elements.elementDialog.tag')}</div>
            <ChooseLabelSelect
              onAdd={(label: MaterialV1Label) => {
                handleAddLabel(label);
                setState({
                  labels: [label],
                });
              }}
              onChange={(id: string) => {
                const selectedLabel = labels?.find((label) => label.id === id);
                setState({
                  labels: selectedLabel ? [selectedLabel] : [],
                });
              }}
              value={state?.labels?.[0]?.id}
              options={labels}
              placeholder={t('elements.elementDialog.tagPlaceholder')}
            />
          </div>

          <TextAreaField
            rows={6}
            labelClassName="sm:text-sm"
            initialValue={state?.description}
            onChange={(e) => updateDescription(e.target.value)}
            placeholder={t('elements.elementDialog.descriptionPlaceholder')}
            label={t('elements.elementDialog.description')}
            fieldClassName={cn('hide-scrollbar', 'sm:bg-system-black16')}
            readOnly={false}
            required
            errorMsg={!state?.description?.trim()?.length ? t('elements.elementDialog.required') : ''}
            maxLength={MAX_TEXTAREA_INPUT_LENGTH.description}
            checkChangeOnBlur={(change) => {
              flushUpdateDescription();
              change &&
                reportEvent('description_modification', {
                  modification_position: 'description',
                });
            }}
          />
        </div>
        {/* Footer */}
        <DialogFooter
          className={cn(
            'mt-3 block shrink-0 space-x-0 px-6 pb-0 sm:fixed sm:right-0 sm:bottom-0 sm:left-0 sm:mt-0 sm:bg-[#191D25] sm:py-4',
          )}
        >
          <div className="flex h-11 w-full justify-center gap-3">
            <Button
              className="h-full min-w-[184px] rounded-12 disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24 sm:flex-1"
              variant="solid"
              disabled={
                !state?.name?.trim()?.length ||
                (state?.name?.trim()?.length || 0) > MAX_INPUT_LENGTH ||
                !state?.description?.trim()?.length ||
                checkLoading ||
                !!nameCheckMsg ||
                submitLoading
              }
              onClick={() => {
                const { description, ...restState } = state || {};

                submit({
                  ...restState,
                  name: state.name?.trim(),
                  recaption: {
                    description,
                  },
                });

                reportEvent('element_click', {
                  element_name: 'confirm',
                });
              }}
            >
              {t('elements.elementDialog.confirm')}
              {submitLoading && <Loading className="ml-2 h-5 w-5 animate-spin text-black" />}
            </Button>
          </div>
          <DialogFooterTip />
        </DialogFooter>
      </>
    );
  },
);

TextElementForm.displayName = 'TextElementForm';
